import { useMemo } from "react";
import type { EffortScale } from "@heraion/shared";
import type { LoggerHostDeps } from "@heraion/ui";

import { useProfileUnitHeldWhile, useShellProfile } from "../ThemeProfileBridge";

// The web's LoggerProfile, in one place.
//
// `LoggerProfile` is `Omit<Profile, 'id'|'createdAt'|'updatedAt'>`, and the
// shell's profile doc is an untyped bag that starts `null` before the load
// (web has no ProfileSnapshot projection like mobile's always-defaulted
// useProfile()). `useLoggerScreen` reads `profile.effortScale` with NO `??` of
// its own, so it needs a real default here (EffortScreen's rir). Everything
// else the Logger reads is optional-chained downstream.
// #343: null until the doc is read. A kg default here wrote a lb athlete's
// sets in kg, so the surfaces this feeds do not open before it. While `open`
// (the Logger or the editor on screen) the unit stays the one it opened with.
//
// ⭐ PE-39/K5 — lifted out of useLoggerHostDeps because it has a SECOND caller
// now. The prescribe editor is the only program editor there is, and the web
// mounts it from PlanScreen, which needs the same profile but none of the rest
// of the host-deps bag (programs, maxes, targets, the offline-queue stubs):
// calling the whole hook there would have fired three network reads to obtain
// one object [[feedback_reuse_nearest_sibling]].
export function useLoggerProfile(open = false): LoggerHostDeps["profile"] | null {
  const { doc } = useShellProfile();
  const unit = useProfileUnitHeldWhile(open);
  return useMemo(
    () =>
      doc === null || unit === null
        ? null
        : ({
            ...doc,
            name: (doc["name"] as string | undefined) ?? "",
            unitsPreference: unit,
            effortScale: (doc["effortScale"] as EffortScale | undefined) ?? "rir",
          } as LoggerHostDeps["profile"]),
    [doc, unit],
  );
}
